import React, { useState, useEffect } from 'react';
import { WhiteSection } from 'components/base/layout-components';
import * as TrackingService from 'common/services/tracking.service';
import { SPACES } from 'common/constants/sizes';
import {
  TRACKING,
  PROVIDERS_FETCH_LARGE_REPOS_FF
} from 'common/constants/constants';
import { createReauthModalOptions } from 'modals/modal-options-factory';
import { omitFromQs, debouncedToggleSnackBar } from 'common/utils/utils';
import QueryString from 'qs';
import {
  onApproveReauth,
  onApproveReauthV2,
  getTableData
} from './repositories.service';
import { AUTH, FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import GitIntegrations from './gitIntegrations/GitIntegrations';
import { teamTooltip } from './repositoriesSections';
import RepositorySettings from './RepositorySettings';
import withGitIntegrationsModals from './gitIntegrations/withGitIntegrationsModals';
import { useLocation } from 'react-router-dom';
import useSettingsService from 'components/hooks/useSettingsService';
import GitStreamRepositoriesTableHeader from 'views/root/views/home/views/settings/views/RepositoriesV2/gitStreamIntegration/GitStreamRepositoriesTableHeader';
import get from 'lodash/get';
import { getSensorAuthorizations as getSensorAuthorizationsSelector } from 'state/selectors/sensors-authorizations';
import { useSelector } from 'react-redux';
import useUpdateCmRepo from './useUpdateCmRepo';

const getTableConfig = ({
  showGitStreamColumn = false,
  sensorAuthId,
  isGitStreamHeaderCheckboxEnabled,
  readonly
}) => [
  {
    name: 'REPOSITORY',
    dataPoint: 'name',
    tableCellProps: { style: { width: '50%', wordBreak: 'break-all' } }
  },
  {
    name: 'TRACKED SINCE',
    dataPoint: 'tracked_since',
    tableCellProps: { style: { borderRight: 0, width: '10%' } }
  },
  {
    name: 'STATUS',
    dataPoint: 'state',
    tableCellProps: { style: { borderLeft: 0, width: '20%' } }
  },
  {
    name: 'PROVIDER LAST ACTIVITY',
    dataPoint: 'provider_last_activity',
    tableCellProps: { style: { width: '15%' } }
  },
  ...(showGitStreamColumn && isGitStreamHeaderCheckboxEnabled && !readonly
    ? [
        {
          name: 'GITSTREAM',
          dataPoint: 'is_gitstream_enabled',
          tableCellProps: { style: { width: '15%' } },
          ColumnHeaderComponent: () => (
            <GitStreamRepositoriesTableHeader sensorAuthId={sensorAuthId} />
          )
        }
      ]
    : []),
  ...(!readonly ? [{ name: 'SETTINGS', dataPoint: 'settings' }] : [])
];

const Repositories = ({
  updateRepository,
  deleteRepository,
  toggleModal,
  repositories,
  createRepositories,
  account,
  setIsProcessingRepos,
  getRepositories,
  history,
  gitIntegrations,
  updateSensorAuthorization,
  license,
  isTeamLicense,
  toggleSnackBar,
  getSensorAuthorizations,
  deleteSensorAuthorization,
  openRepositoriesModal,
  apiTokens,
  setApiTokens,
  readonly,
  selectionProps,
  fetchRepositoriesExtraParams = {}
}) => {
  const [term, setTerm] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [pickedRepoName, setPickedRepoName] = useState('');
  const [pickedRepoId, setPickedRepoId] = useState('');
  const [saving, setSaving] = useState('');
  const [repoSettings, setRepoSettings] = useState('');
  const sensorAuthorizations = useSelector(getSensorAuthorizationsSelector);
  const location = useLocation();
  const { getSettingOrDefinitionValue, getSettingsByDefinitionLevel } =
    useSettingsService();
  const isReauthV2Enabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isReauthV2Enabled
  });
  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
    setTerm('');
  };
  const { created_sensor, sensor_auth_id, ...search } = QueryString.parse(
    location.search,
    {
      ignoreQueryPrefix: true
    }
  );

  const cacheId = QueryString.parse(location.search, {
    ignoreQueryPrefix: true
  })[AUTH.OAUTH_SECRET_CACHE_ID];

  const isGitstreamFlow = QueryString.parse(location.search, {
    ignoreQueryPrefix: true
  })['gitstreamFlow'];

  useUpdateCmRepo({ repositories });

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.GIT_INTEGRATIONS,
      num_of_integrations: gitIntegrations.length
    });

    if (created_sensor) {
      history.replace({ search: QueryString.stringify(search) });
      const createdSensor = gitIntegrations.find(
        ({ id }) => id === +created_sensor
      );
      if (createdSensor) {
        openRepositoriesModal({
          sensorAuth: createdSensor,
          isAddIntegrationFlow: true
        });
      }
    }
  }, []);

  useEffect(() => {
    if (cacheId) {
      const sensor_authorization_id = parseInt(sensor_auth_id);
      const sensorAuthorization = gitIntegrations.find(
        ({ id }) => id === parseInt(sensor_auth_id)
      );
      const { sensor_name } = sensorAuthorization;
      const isFetchLargeRepos = getSettingOrDefinitionValue({
        name: get(PROVIDERS_FETCH_LARGE_REPOS_FF, [sensor_name])
      });
      omitFromQs(AUTH.OAUTH_SECRET_CACHE_ID, search);
      toggleModal(
        createReauthModalOptions({
          toggleModal,
          toggleSnackBar,
          openRepositoriesModal,
          ...(isGitstreamFlow && { isGitstreamFlow }),
          onApproveReauth: args =>
            onApproveReauth(
              args,
              getSensorAuthorizations,
              getRepositories,
              sensor_name,
              isGitstreamFlow
            ),
          onApproveReauthV2: args =>
            onApproveReauthV2(
              args,
              getSensorAuthorizations,
              sensor_name,
              isGitstreamFlow
            ),
          reauthParams: {
            cacheId,
            sensor_auth_id
          },
          gitIntegrations,
          sensor_authorization_id,
          cacheId,
          sensorAuthorization,
          isFetchLargeRepos,
          isReauthV2Enabled
        })
      );
    }
  }, [cacheId]);

  const deleteGitIntegrationFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.deleteGitIntegration
  });
  const { maxRepos } = license;
  const canAddMoreRepos =
    !maxRepos ||
    maxRepos - repositories.filter(({ state }) => state === 'active').length >
      0;
  const getReposPerSensorAuth = id =>
    repositories.filter(
      ({ sensor_authorization_id }) => sensor_authorization_id === id
    );
  const integrations = gitIntegrations.map(
    ({
      sensor_type_id,
      id,
      name,
      sensor_name,
      state,
      self_hosted_uri,
      is_on_prem,
      gitstream_enabled,
      pr_stats: prStats,
      opa_gitstream_installed,
      is_multi_token_enabled: isMultiTokenEnabled,
      child_sensor_auth_id,
      profile
    }) => ({
      sensor_type_id,
      id,
      name,
      sensor_name,
      state,
      url: self_hosted_uri,
      repos: getReposPerSensorAuth(id),
      is_on_prem,
      gitstream_enabled,
      prStats,
      isMultiTokenEnabled,
      opa_gitstream_installed,
      child_sensor_auth_id,
      profile
    })
  );
  return (
    <WhiteSection t={SPACES['x-medium']}>
      {isSettingsOpen ? (
        <RepositorySettings
          pickedRepoName={pickedRepoName}
          toggleSettings={toggleSettings}
          repoSettings={repoSettings}
          pickedRepoId={pickedRepoId}
          toggleModal={toggleModal}
          apiTokens={apiTokens}
          setApiTokens={setApiTokens}
        />
      ) : (
        <GitIntegrations
          gitIntegrations={gitIntegrations}
          isTeamLicense={isTeamLicense}
          teamTooltip={teamTooltip}
          integrations={integrations}
          account={account}
          deleteGitIntegrationFF={deleteGitIntegrationFF}
          updateSensorAuthorization={updateSensorAuthorization}
          canAddMoreRepos={canAddMoreRepos}
          toggleModal={toggleModal}
          repositories={repositories}
          createRepositories={createRepositories}
          setIsProcessingRepos={setIsProcessingRepos}
          toggleSnackBar={toggleSnackBar}
          getSensorAuthorizations={getSensorAuthorizations}
          getRepositories={getRepositories}
          deleteSensorAuthorization={deleteSensorAuthorization}
          getTableData={repositories =>
            getTableData({
              repositories,
              term,
              updateRepository,
              debouncedToggleSnackBar,
              saving,
              setSaving,
              setRepoSettings,
              getSettingsByDefinitionLevel,
              setPickedRepoName,
              setPickedRepoId,
              setIsSettingsOpen,
              deleteRepository,
              toggleSnackBar,
              getSensorAuthorizations,
              getRepositories,
              sensorAuthorizations,
              readonly,
              selectionProps
            })
          }
          tableConfig={getTableConfig}
          setTerm={setTerm}
          readonly={readonly}
          selectionProps={selectionProps}
          fetchRepositoriesExtraParams={fetchRepositoriesExtraParams}
        />
      )}
    </WhiteSection>
  );
};
export default withGitIntegrationsModals(Repositories);
